import { useRef, useState } from 'react';

import { eventHeads } from './projection';
import { clientApi } from '@/config/axios';
import type { ClinicalEvent } from './types';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { consentFollowupTasks } from './consentTasks';
import { flowsheetUrl, useEventActions } from './api';
import { usePermissions } from '@/features/permissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
interface Candidate {
  id: string;
  name: string;
  revision_id: string;
  revision_number: number;
}
interface Preview {
  expected_head_id: string;
  revision_id: string;
  sha256: string;
  url: string;
}
export function ConsentArtifacts({ events, episodeId }: { episodeId: string; events: ClinicalEvent[] }) {
  const { organization } = useAuth();
  const { permissions } = usePermissions();
  const actions = useEventActions(episodeId);
  const base = `${flowsheetUrl(organization?.organization_id ?? '')}/episodes/${episodeId}`;
  const selectionGeneration = useRef(0);
  const decisions = eventHeads(events).filter((e) => e.event_type === 'CONSENT.DECISION' && e.status !== 'STRUCK');
  const tasks = consentFollowupTasks(events);
  const [selected, setSelected] = useState<ClinicalEvent | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const load = async (event: ClinicalEvent) => {
    const generation = ++selectionGeneration.current;
    setBusy(true);
    setSelected(event);
    setPreview(null);
    setReviewed(false);
    setCandidates([]);
    setMessage('');
    setRequestId(crypto.randomUUID());
    try {
      const rows = (await clientApi.get<Candidate[]>(`${base}/consents/${event.root_id}/documents`)).data;
      if (generation === selectionGeneration.current) setCandidates(rows);
    } catch {
      if (generation === selectionGeneration.current) setMessage('Linked documents could not be loaded.');
    } finally {
      if (generation === selectionGeneration.current) setBusy(false);
    }
  };
  const open = async (revision: string) => {
    setBusy(true);
    setReviewed(false);
    try {
      const current = (
        await clientApi.get<Preview>(`${base}/consents/${selected!.root_id}/documents/${revision}/preview`)
      ).data;
      if (current.expected_head_id !== selected!.id) throw new Error('Decision changed; reload review');
      setPreview(current);
      setRequestId(crypto.randomUUID());
    } catch {
      setMessage('Document preview unavailable.');
    } finally {
      setBusy(false);
    }
  };
  const attest = async () => {
    if (!selected || !preview) return;
    setBusy(true);
    setMessage('');
    try {
      const artifact = (
        await clientApi.post<{ id: string }>(`${base}/consents/${selected.root_id}/artifacts`, {
          request_id: requestId,
          attestation: note.trim(),
          expected_sha256: preview.sha256,
          revision_id: preview.revision_id,
          patient_signature_reviewed: true,
          expected_head_id: preview.expected_head_id,
        })
      ).data;
      await actions.mutateAsync({
        rootId: selected.root_id,
        input: {
          kind: 'amend',
          client_seq: 1,
          device_id: requestId,
          expected_head_id: preview.expected_head_id,
          reason: 'Linked clinician-reviewed patient-signature evidence',
          payload: { ...selected.payload, form_document_id: artifact.id },
        },
      });
      setSelected(null);
      setPreview(null);
      setNote('');
      setMessage('Signature evidence linked. The clinical correction may require review or cosigning.');
    } catch {
      setMessage(
        'Evidence was not fully linked. Your review is retained; refresh the decision before retrying if it changed.',
      );
    } finally {
      setBusy(false);
    }
  };
  if (!organization || !permissions.includes('read_uploaded_document')) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Consent forms and follow-up</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p>
          {tasks.length} declined decisions need a linked reviewed form. Each task is tied to its original decision, so
          retrying does not create duplicate tasks. Clinician review records observed patient-signature evidence; it
          does not determine legal validity.
        </p>
        {decisions.map((e) => (
          <div key={e.root_id} className="flex flex-wrap gap-2 border p-2">
            <span>
              {String(e.payload.test)} · {String(e.payload.decision)} ·{' '}
              {e.payload.form_document_id ? 'Form linked' : 'No reviewed form'}
            </span>
            {permissions.includes('attest_consent_artifact') &&
              permissions.includes('amend_clinical_event') &&
              e.status === 'ACTIVE' && (
                <Button disabled={busy} variant="outline" onClick={() => void load(e)}>
                  Review and link form
                </Button>
              )}
            {!!e.payload.form_document_id && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const data = (
                      await clientApi.get<{ url: string }>(`${base}/consent-artifacts/${e.payload.form_document_id}`)
                    ).data;
                    window.open(data.url, '_blank', 'noopener,noreferrer');
                  } catch {
                    setMessage('Retained evidence could not be verified or opened.');
                  }
                }}>
                Open retained evidence
              </Button>
            )}
          </div>
        ))}
        {selected && (
          <fieldset disabled={busy} className="space-y-3 border p-3">
            <legend>Review form for {String(selected.payload.test)}</legend>
            {!candidates.length && (
              <p>
                No uploaded document is linked to this decision’s encounter. Upload the form and link it to that
                encounter first.
              </p>
            )}
            {candidates.map((c) => (
              <Button key={c.revision_id} variant="outline" onClick={() => void open(c.revision_id)}>
                {c.name} · revision {c.revision_number}
              </Button>
            ))}
            {preview && (
              <>
                <a target="_blank" href={preview.url} rel="noopener noreferrer" className="block underline">
                  Open the exact source revision to review
                </a>
                <label className="block">
                  <textarea
                    rows={3}
                    value={note}
                    maxLength={2000}
                    className="w-full border"
                    placeholder="Record what you verified, including the patient, decision and signature"
                    onChange={(e) => {
                      setNote(e.target.value);
                      setReviewed(false);
                      setRequestId(crypto.randomUUID());
                    }}
                  />
                </label>
                <label>
                  <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} /> I
                  reviewed this document and verified it contains the patient’s signature for this decision.
                </label>
                <Button disabled={!reviewed || note.trim().length < 10 || busy} onClick={() => void attest()}>
                  Retain reviewed evidence and link
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setSelected(null)}>
              Close review
            </Button>
          </fieldset>
        )}
        {message && <p role="status">{message}</p>}
      </CardContent>
    </Card>
  );
}
